import React, { useRef, useMemo, useCallback, useEffect } from 'react'
import ReactDOM from 'react-dom'
import JoditEditor from 'jodit-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpandAlt } from '@fortawesome/pro-solid-svg-icons'
import classNames from 'classnames'
import IFormFieldProps from 'interfaces/form-field-props'
import i18n from 'i18n'
import ErrorField from '../error-field/ErrorField'
import InputLabel from '../input-label/InputLabel'
import styles from './RichTextInput.module.scss'
import './jodit.min.css'
import 'jodit/build/jodit.min.js'

const defaultConfig = {
  language: 'Auto',
  link: {
    noFollowCheckbox: false,
  },
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  toolbarAdaptive: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  disablePlugins: 'placeholder',
  buttons:
    'source,|,bold,underline,strikethrough,italic,|,ul,ol,,outdent,indent,,font,fontsize,brush,|,image,video,table,link,|,align,undo,redo,copyformat',
  height: 150,
  uploader: {
    insertImageAsBase64URI: true,
  },
  schema: {
    tags: {
      iframe: {
        attributes: {
          src: true,
          width: true,
          height: true,
          frameborder: true,
          allowfullscreen: true,
          referrerpolicy: true,
        },
      },
    },
  },
}

interface IRichText extends IFormFieldProps {
  config?: any
  labelClassName?: string
}

const RichTextInput: React.FC<IRichText> = ({
  label,
  value = '',
  config,
  onChange,
  error = '',
  disabled,
  labelClassName,
}) => {
  const editor = useRef(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const mountResizeIcon = () => {
      const handle = wrapper.querySelector<HTMLElement>('.jodit-editor__resize')
      if (!handle) return

      let iconHost = handle.querySelector<HTMLElement>(
        `[data-rich-text-resize-icon="true"]`
      )

      if (!iconHost) {
        handle.innerHTML = ''
        iconHost = document.createElement('span')
        iconHost.dataset.richTextResizeIcon = 'true'
        iconHost.className = styles.resizeHandleIcon
        handle.appendChild(iconHost)
        ReactDOM.render(
          <FontAwesomeIcon icon={faExpandAlt} rotation={90} aria-hidden />,
          iconHost
        )
      }
    }

    mountResizeIcon()
    const observer = new MutationObserver(mountResizeIcon)
    observer.observe(wrapper, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      wrapper
        .querySelectorAll('[data-rich-text-resize-icon="true"]')
        .forEach((iconHost) => {
          ReactDOM.unmountComponentAtNode(iconHost)
        })
    }
  }, [])

  // HACK: Text Area loses focus when change the config.
  // https://github.com/jodit/jodit-react/issues/43
  const applyConfig = useMemo(
    () => ({
      ...defaultConfig,
      language: i18n.language,
      ...config,
      disabled: disabled,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled]
  )

  const handleContentChange = useCallback(
    (newContent) => {
      const parser = new DOMParser()
      const doc = parser.parseFromString(newContent, 'text/html')
      const iframes = doc.querySelectorAll('iframe')

      iframes.forEach((iframe) => {
        const src = iframe.getAttribute('src')
        if (src && src.toLowerCase().includes('youtube')) {
          // Fix YouTube Shorts URLs: replace /embed/shorts/ with /embed/
          const fixedSrc = src.replace(/\/embed\/shorts\//, '/embed/')
          iframe.setAttribute('src', fixedSrc)

          iframe.setAttribute(
            'referrerpolicy',
            'strict-origin-when-cross-origin'
          )
        }
      })
      const modifiedContent = doc.body.innerHTML

      if (onChange) {
        onChange(modifiedContent)
      }
    },
    [onChange]
  )

  return (
    <>
      <InputLabel label={label || ''} className={labelClassName} />
      <div
        ref={wrapperRef}
        className={classNames(styles.editorWrapper, {
          [styles.hasError]: !!error,
        })}
      >
        <JoditEditor
          ref={editor}
          value={value}
          config={applyConfig}
          onChange={handleContentChange}
          onBlur={() => document.querySelector('.jodit-popup')?.remove()}
        />
      </div>
      {error ? <ErrorField text={error} /> : null}
    </>
  )
}

export default RichTextInput
